'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { VideoForm, type VideoFormRef } from '@/components/partials/videos'
import { type CreateVideoParams } from '@/types/video'
import { useCreateVideo } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const videoFormRef = useRef<VideoFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createVideo, isPending: isLoading } = useCreateVideo()

  const handleSubmit = (data: CreateVideoParams) => {
    createVideo(data, {
      onSuccess: async newVideo => {
        setIsRedirecting(true)
        queryClient.setQueryData(['video', newVideo.id], newVideo)
        await queryClient.invalidateQueries({ queryKey: ['videos'] })
        toast.success(t('created_successfully'))
        router.push(`/galleries/videos/${newVideo.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateVideoParams) => {
    createVideo(data, {
      onSuccess: async newVideo => {
        queryClient.setQueryData(['video', newVideo.id], newVideo)
        await queryClient.invalidateQueries({ queryKey: ['videos'] })
        const detailUrl = `/galleries/videos/${newVideo.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        videoFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (videoFormRef.current) {
        videoFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/galleries/videos')
  }

  return (
    <VideoForm
      key={formKey}
      ref={videoFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
